import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Loader2,
  CheckCircle2,
  KeyRound,
  Shield,
  BookOpen,
  Globe,
  Mic,
  FileText,
  Star,
  Calendar,
  Mail,
  Phone,
  AlertCircle,
} from "lucide-react";
import { useTenant } from "../contexts/TenantContext";
import { fonts as defaultFonts } from "../styles/theme";

const API_BASE = "/api";

const FEATURES = [
  {
    icon: BookOpen,
    title: "Research Library",
    desc: "Reports, whitepapers, and research notes",
  },
  {
    icon: Globe,
    title: "Geostrategy Insights",
    desc: "Exclusive geopolitical analysis and market intelligence",
  },
  {
    icon: Mic,
    title: "Webcast Access",
    desc: "Live sessions and archived recordings",
  },
  {
    icon: FileText,
    title: "Newsletter Archives",
    desc: "Recent editions",
  },
];

const PREMIUM = [
  { icon: Star, title: "Principal Access" },
  { icon: Shield, title: "Advisory Services" },
  { icon: Calendar, title: "Premium Events" },
];

export default function ChairmanAccess() {
  const tenant = useTenant();
  const sb = tenant.theme?.sidebar || {};
  const fonts = tenant.theme?.fonts || defaultFonts;
  const accent = tenant.newsletter?.accentColor || "#4E95D9";

  const [searchParams] = useSearchParams();
  const emailParam = searchParams.get("email") || "";
  const verifyParam = searchParams.get("verify") || "";
  const tsParam = searchParams.get("ts") || "";
  const paymentStatus = searchParams.get("payment");

  // Page state: email_input | verification_sent | verifying | verified_subscriber | verified_form | payment_success | payment_cancelled
  const [phase, setPhase] = useState(() => {
    if (paymentStatus === "success") return "payment_success";
    if (paymentStatus === "cancelled") return "payment_cancelled";
    if (verifyParam && tsParam) return "verifying";
    return "email_input";
  });

  const [email, setEmail] = useState(emailParam);
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState(null);
  const [contact, setContact] = useState(null);
  const [error, setError] = useState(null);

  // Form fields for non-subscriber signup
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    company: "",
    phone: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  // Store verify credentials for POST
  const [verifyCredentials, setVerifyCredentials] = useState({
    verify: verifyParam,
    ts: tsParam,
  });

  // On mount: if verify params present, auto-verify
  useEffect(() => {
    if (phase === "verifying" && verifyParam && tsParam && emailParam) {
      verifyEmail(emailParam, verifyParam, tsParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function verifyEmail(emailAddr, token, ts) {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        email: emailAddr,
        verify: token,
        ts,
      });
      const res = await fetch(`${API_BASE}/chairmans-access?${params}`);
      const data = await res.json();
      setInfo(data);

      if (data.verified === false) {
        setError(
          data.verifyError === "expired"
            ? "This verification link has expired. Please request a new one."
            : "Invalid verification link. Please try again.",
        );
        setPhase("email_input");
        return;
      }

      if (data.verified) {
        setVerifyCredentials({ verify: token, ts });
        if (data.isSubscriber) {
          setContact(data.contact);
          setPhase("verified_subscriber");
        } else {
          if (data.contact) {
            setContact(data.contact);
            setForm({
              firstName: data.contact.firstName || "",
              lastName: data.contact.lastName || "",
              company: data.contact.company || "",
              phone: data.contact.phone || "",
            });
          }
          setPhase("verified_form");
        }
      }
    } catch (err) {
      setError("Failed to verify. Please try again.");
      setPhase("email_input");
    } finally {
      setLoading(false);
    }
  }

  async function handleSendVerification(e) {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/chairmans-access-verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to send verification");
      setPhase("verification_sent");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleRequestAccess(e) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/chairmans-access`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          company: form.company.trim(),
          phone: form.phone.trim(),
          verify: verifyCredentials.verify,
          ts: verifyCredentials.ts,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Request failed");
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      } else {
        setSuccess(
          data.message ||
            "Your request has been received. Our team will contact you shortly.",
        );
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  const headerBg = sb.bg || "#000f2d";

  // --- Shared styles ---
  const inputStyle = {
    width: "100%",
    padding: "12px 14px",
    fontSize: 14,
    border: "1px solid #ddd",
    borderRadius: 8,
    outline: "none",
    boxSizing: "border-box",
    fontFamily: fonts.body,
  };

  const btnPrimary = {
    width: "100%",
    padding: "14px 24px",
    fontSize: 15,
    fontWeight: 700,
    fontFamily: fonts.body,
    background: accent,
    color: "#fff",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  };

  // --- Header section (always shown) ---
  const headerSection = (
    <div
      style={{
        background: headerBg,
        padding: "48px 20px 56px",
        textAlign: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: `linear-gradient(135deg, ${headerBg} 0%, rgba(0,0,0,0.3) 100%)`,
        }}
      />
      <div
        style={{
          position: "relative",
          zIndex: 1,
          maxWidth: 700,
          margin: "0 auto",
        }}
      >
        {tenant.logo && (
          <img
            src={tenant.logo}
            alt={tenant.name}
            style={{ height: 108, marginBottom: 24 }}
          />
        )}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
            marginBottom: 12,
          }}
        >
          <KeyRound size={28} color={accent} />
          <h1
            style={{
              fontFamily: fonts.heading,
              fontSize: 32,
              color: "#ffffff",
              margin: 0,
              fontWeight: 700,
            }}
          >
            Chairman&apos;s Access
          </h1>
        </div>
        <p
          style={{
            fontFamily: fonts.body,
            fontSize: 17,
            color: "rgba(255,255,255,0.8)",
            maxWidth: 500,
            margin: "0 auto",
            lineHeight: 1.6,
            fontStyle: "italic",
          }}
        >
          Curated intelligence and analysis for busy Executives.
        </p>
      </div>
    </div>
  );

  // --- Feature cards ---
  const featureCards = (
    <div style={{ marginBottom: 28 }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 14,
          marginBottom: 20,
        }}
      >
        {FEATURES.map((f, i) => (
          <div
            key={i}
            style={{
              background: "#fff",
              borderRadius: 10,
              padding: 20,
              boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
              border: "1px solid #eee",
            }}
          >
            <f.icon size={22} color={accent} style={{ marginBottom: 8 }} />
            <h3
              style={{
                fontFamily: fonts.heading,
                fontSize: 14,
                color: "#1a1a2e",
                marginBottom: 4,
              }}
            >
              {f.title}
            </h3>
            <p
              style={{
                fontSize: 12,
                color: "#666",
                lineHeight: 1.5,
                margin: 0,
              }}
            >
              {f.desc}
            </p>
          </div>
        ))}
      </div>

      {/* Premium tier badges */}
      <div
        style={{
          display: "flex",
          gap: 12,
          justifyContent: "center",
          flexWrap: "wrap",
        }}
      >
        {PREMIUM.map((p, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 18px",
              background: headerBg,
              color: "#fff",
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              fontFamily: fonts.body,
              border: `1px solid ${accent}30`,
            }}
          >
            <p.icon size={15} color={accent} />
            {p.title}
          </div>
        ))}
      </div>
    </div>
  );

  // --- Error banner ---
  const errorBanner = error ? (
    <div
      style={{
        padding: "10px 14px",
        marginBottom: 16,
        borderRadius: 8,
        background: "rgba(220,38,38,0.08)",
        border: "1px solid rgba(220,38,38,0.2)",
        color: "#dc2626",
        fontSize: 13,
        textAlign: "center",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
      }}
    >
      <AlertCircle size={15} />
      {error}
    </div>
  ) : null;

  // --- Card wrapper ---
  const card = (children) => (
    <div
      style={{
        background: "#fff",
        borderRadius: 12,
        padding: 32,
        boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
        border: "1px solid #eee",
      }}
    >
      {children}
    </div>
  );

  // --- Render content based on phase ---
  let content;

  if (phase === "payment_success") {
    content = card(
      <div style={{ textAlign: "center", padding: 24 }}>
        <CheckCircle2 size={48} color="#10b981" style={{ marginBottom: 16 }} />
        <h2
          style={{
            fontFamily: fonts.heading,
            fontSize: 22,
            color: "#1a1a2e",
            marginBottom: 8,
          }}
        >
          Payment Successful!
        </h2>
        <p
          style={{
            color: "#666",
            fontSize: 14,
            maxWidth: 400,
            margin: "0 auto",
            lineHeight: 1.6,
          }}
        >
          Welcome to Chairman&apos;s Access. Your annual subscription is now
          active. You&apos;ll receive a confirmation email shortly.
        </p>
      </div>,
    );
  } else if (phase === "payment_cancelled") {
    content = card(
      <div style={{ textAlign: "center", padding: 24 }}>
        <KeyRound size={48} color={accent} style={{ marginBottom: 16 }} />
        <h2
          style={{
            fontFamily: fonts.heading,
            fontSize: 22,
            color: "#1a1a2e",
            marginBottom: 8,
          }}
        >
          Checkout Cancelled
        </h2>
        <p
          style={{
            color: "#666",
            fontSize: 14,
            maxWidth: 400,
            margin: "0 auto",
            lineHeight: 1.6,
            marginBottom: 20,
          }}
        >
          No worries — your checkout was not completed. You can try again
          whenever you&apos;re ready.
        </p>
        <button
          onClick={() => {
            setPhase("email_input");
            setError(null);
          }}
          style={{ ...btnPrimary, maxWidth: 200, margin: "0 auto" }}
        >
          Try Again
        </button>
      </div>,
    );
  } else if (phase === "verifying" || (phase === "email_input" && loading)) {
    content = card(
      <div style={{ textAlign: "center", padding: 32 }}>
        <Loader2
          size={28}
          color={accent}
          className="spin"
        />
        <p style={{ marginTop: 12, color: "#666", fontSize: 14 }}>
          {phase === "verifying"
            ? "Verifying your email…"
            : "Sending verification…"}
        </p>
      </div>,
    );
  } else if (phase === "verification_sent") {
    content = card(
      <div style={{ textAlign: "center", padding: 24 }}>
        <Mail size={48} color={accent} style={{ marginBottom: 16 }} />
        <h2
          style={{
            fontFamily: fonts.heading,
            fontSize: 22,
            color: "#1a1a2e",
            marginBottom: 8,
          }}
        >
          Check Your Email
        </h2>
        <p
          style={{
            color: "#666",
            fontSize: 14,
            maxWidth: 400,
            margin: "0 auto",
            lineHeight: 1.6,
            marginBottom: 8,
          }}
        >
          We&apos;ve sent a verification link to{" "}
          <strong style={{ color: "#1a1a2e" }}>{email}</strong>. Click the link
          in the email to continue.
        </p>
        <p
          style={{
            color: "#b45309",
            fontSize: 12,
            background: "rgba(245,158,11,0.1)",
            borderRadius: 6,
            padding: "8px 12px",
            maxWidth: 400,
            margin: "12px auto 0",
            lineHeight: 1.5,
          }}
        >
          Can&apos;t find it? Please check your spam or junk folder.
        </p>
        <p style={{ color: "#999", fontSize: 12, marginTop: 16 }}>
          The link expires in 15 minutes.{" "}
          <button
            onClick={() => {
              setPhase("email_input");
              setError(null);
            }}
            style={{
              background: "none",
              border: "none",
              color: accent,
              cursor: "pointer",
              fontSize: 12,
              fontFamily: fonts.body,
              textDecoration: "underline",
              padding: 0,
            }}
          >
            Use a different email
          </button>
        </p>
      </div>,
    );
  } else if (phase === "verified_subscriber") {
    content = card(
      <div style={{ textAlign: "center", padding: 24 }}>
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            background: `${accent}18`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 16px",
          }}
        >
          <KeyRound size={32} color={accent} />
        </div>
        <h2
          style={{
            fontFamily: fonts.heading,
            fontSize: 22,
            color: "#1a1a2e",
            marginBottom: 8,
          }}
        >
          You&apos;re Already a Subscriber
        </h2>
        <p
          style={{
            color: "#666",
            fontSize: 14,
            lineHeight: 1.6,
            marginBottom: 16,
          }}
        >
          {contact?.firstName ? `${contact.firstName}, you` : "You"} already
          have full Chairman&apos;s Access.
        </p>
        {contact && (
          <div
            style={{
              background: "#f8f9fa",
              borderRadius: 10,
              padding: "16px 20px",
              textAlign: "left",
              maxWidth: 320,
              margin: "0 auto",
            }}
          >
            <div
              style={{
                fontSize: 12,
                color: "#999",
                marginBottom: 8,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              Your Account
            </div>
            {(contact.firstName || contact.lastName) && (
              <div style={{ fontSize: 14, color: "#1a1a2e", marginBottom: 4 }}>
                {contact.firstName} {contact.lastName}
              </div>
            )}
            <div style={{ fontSize: 13, color: "#666", marginBottom: 2 }}>
              {contact.email}
            </div>
            {contact.company && (
              <div style={{ fontSize: 13, color: "#666", marginBottom: 2 }}>
                {contact.company}
              </div>
            )}
            {contact.phone && (
              <div style={{ fontSize: 13, color: "#666" }}>{contact.phone}</div>
            )}
          </div>
        )}
        <p style={{ color: "#999", fontSize: 12, marginTop: 20 }}>
          Questions? Contact{" "}
          <a href={`mailto:${tenant.contactEmail}`} style={{ color: accent }}>
            {tenant.contactEmail}
          </a>
        </p>
      </div>,
    );
  } else if (phase === "verified_form") {
    const phoneValid = form.phone.trim().length > 0;
    content = card(
      <>
        <h2
          style={{
            fontFamily: fonts.heading,
            fontSize: 20,
            color: "#1a1a2e",
            marginBottom: 4,
            textAlign: "center",
          }}
        >
          {info?.price ? `Get Access — ${info.price}` : "Request Access"}
        </h2>
        <p
          style={{
            fontSize: 13,
            color: "#888",
            textAlign: "center",
            marginBottom: 24,
          }}
        >
          {info?.price
            ? "Complete the form below to proceed to checkout"
            : "Submit your details and our team will be in touch"}
        </p>

        {errorBanner}

        {success ? (
          <div style={{ textAlign: "center", padding: 16 }}>
            <CheckCircle2
              size={40}
              color="#10b981"
              style={{ marginBottom: 12 }}
            />
            <p
              style={{
                color: "#666",
                fontSize: 14,
                maxWidth: 400,
                margin: "0 auto",
              }}
            >
              {success}
            </p>
          </div>
        ) : (
          <form onSubmit={handleRequestAccess}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
                marginBottom: 12,
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#555",
                    marginBottom: 4,
                  }}
                >
                  First Name
                </label>
                <input
                  value={form.firstName}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, firstName: e.target.value }))
                  }
                  style={inputStyle}
                />
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#555",
                    marginBottom: 4,
                  }}
                >
                  Last Name
                </label>
                <input
                  value={form.lastName}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, lastName: e.target.value }))
                  }
                  style={inputStyle}
                />
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label
                style={{
                  display: "block",
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#555",
                  marginBottom: 4,
                }}
              >
                Email
              </label>
              <input
                type="email"
                value={email}
                readOnly
                style={{ ...inputStyle, background: "#f8f9fa", color: "#888" }}
              />
            </div>

            <div style={{ marginBottom: 12 }}>
              <label
                style={{
                  display: "block",
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#555",
                  marginBottom: 4,
                }}
              >
                Company
              </label>
              <input
                value={form.company}
                onChange={(e) =>
                  setForm((p) => ({ ...p, company: e.target.value }))
                }
                style={inputStyle}
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label
                style={{
                  display: "block",
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#555",
                  marginBottom: 4,
                }}
              >
                Phone Number *
              </label>
              <div style={{ position: "relative" }}>
                <Phone
                  size={15}
                  color="#999"
                  style={{
                    position: "absolute",
                    left: 12,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                />
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, phone: e.target.value }))
                  }
                  placeholder="Where can we reach you?"
                  style={{ ...inputStyle, paddingLeft: 36 }}
                  required
                />
              </div>
              {!phoneValid && (
                <p
                  style={{
                    fontSize: 11,
                    color: "#999",
                    margin: "4px 0 0",
                    fontStyle: "italic",
                  }}
                >
                  A contact number is required to proceed
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={submitting || !phoneValid}
              style={{
                ...btnPrimary,
                opacity: submitting || !phoneValid ? 0.5 : 1,
                cursor: submitting || !phoneValid ? "not-allowed" : "pointer",
              }}
            >
              {submitting ? (
                <Loader2
                  size={16}
                  className="spin"
                />
              ) : (
                <KeyRound size={16} />
              )}
              {info?.price ? "Proceed to Checkout" : "Request Access"}
            </button>
          </form>
        )}
      </>,
    );
  } else {
    // email_input phase
    content = card(
      <>
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <Star size={32} color={accent} style={{ marginBottom: 12 }} />
          <h2
            style={{
              fontFamily: fonts.heading,
              fontSize: 20,
              color: "#1a1a2e",
              marginBottom: 8,
            }}
          >
            Get Started
          </h2>
          <p style={{ fontSize: 13, color: "#888", lineHeight: 1.5 }}>
            Enter your email to verify your identity and check your subscription
            status.
          </p>
        </div>

        {errorBanner}

        <form onSubmit={handleSendVerification}>
          <div style={{ marginBottom: 16 }}>
            <label
              style={{
                display: "block",
                fontSize: 12,
                fontWeight: 600,
                color: "#555",
                marginBottom: 4,
              }}
            >
              Email Address
            </label>
            <div style={{ position: "relative" }}>
              <Mail
                size={15}
                color="#999"
                style={{
                  position: "absolute",
                  left: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                }}
              />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                style={{ ...inputStyle, paddingLeft: 36 }}
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={loading || !email.trim()}
            style={{
              ...btnPrimary,
              opacity: loading || !email.trim() ? 0.5 : 1,
              cursor: loading || !email.trim() ? "not-allowed" : "pointer",
            }}
          >
            {loading ? (
              <Loader2
                size={16}
                className="spin"
              />
            ) : (
              <Mail size={16} />
            )}
            Send Verification Link
          </button>
        </form>
      </>,
    );
  }

  return (
    <div style={{ height: "100vh", overflowY: "auto", background: "#f8f9fa" }}>
      {headerSection}

      <div
        style={{
          maxWidth: 800,
          margin: "-30px auto 40px",
          padding: "0 20px",
          position: "relative",
          zIndex: 2,
        }}
      >
        {/* Show feature cards on email input and verification sent phases */}
        {(phase === "email_input" || phase === "verification_sent") &&
          featureCards}

        {content}

        {/* Footer */}
        <div
          style={{
            textAlign: "center",
            marginTop: 24,
            fontSize: 12,
            color: "#999",
          }}
        >
          &copy; {new Date().getFullYear()}{" "}
          {tenant.legalName || tenant.companyName}
          {tenant.address
            ? ` · ${tenant.address.split(",").slice(-2).join(",").trim()}`
            : ""}
        </div>
      </div>    </div>
  );
}
